import { useCallback } from 'react';

import { DoubleArrow, DownArrow, UpArrow } from 'ui/icons';
import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import {
  EXPERIMENTS_TABLE_COLUMNS,
  CHOOSE_CAMPAIGN_COLUMNS,
  ColumnType,
} from 'modules/experiments/types';
import { InPageStyledHeaderButton, StyledHeaderButton } from './Styled';

type HeaderRowCellProps<T> = {
  column: ColumnType;
  columnId: T;
  isActiveSortColumn: boolean;
  sortDirection: SORT_DIRECTIONS;
  setSortedBy: (sortedBy: T) => void;
  setSortDirection: (sortDirection: SORT_DIRECTIONS) => void;
  inPage?: boolean;
};

export const HeaderRowCell = <T extends EXPERIMENTS_TABLE_COLUMNS | CHOOSE_CAMPAIGN_COLUMNS>({
  column,
  columnId,
  isActiveSortColumn,
  sortDirection,
  setSortDirection,
  setSortedBy,
  inPage = false,
}: HeaderRowCellProps<T>) => {
  const isAscendingSort = sortDirection === SORT_DIRECTIONS.ASCENDING;
  const handleClick = useCallback(() => {
    if (isActiveSortColumn) {
      setSortDirection(isAscendingSort ? SORT_DIRECTIONS.DESCENDING : SORT_DIRECTIONS.ASCENDING);
    } else if (column.isSortable) {
      setSortedBy(columnId);
      setSortDirection(column.initialSortDirection || SORT_DIRECTIONS.DESCENDING);
    }
  }, [
    setSortDirection,
    setSortedBy,
    column.isSortable,
    columnId,
    isAscendingSort,
    isActiveSortColumn,
    column.initialSortDirection,
  ]);
  const isSortableAndActive = column.isSortable && isActiveSortColumn;
  const HeaderButton = inPage ? InPageStyledHeaderButton : StyledHeaderButton;

  return (
    <HeaderButton
      isSortable={column.isSortable}
      isActiveSortColumn={isActiveSortColumn}
      isRightAligned={column.isRightAligned}
      as={column.isSortable ? 'button' : 'div'}
      data-test={`column-header-${column.id}`}
      key={column.id}
      onClick={handleClick}
    >
      <span>{column.label}</span>

      {column.isSortable && !isActiveSortColumn && <DoubleArrow isColorInherited />}
      {isSortableAndActive && isAscendingSort && <UpArrow isColorInherited />}
      {isSortableAndActive && !isAscendingSort && <DownArrow isColorInherited />}
    </HeaderButton>
  );
};
